import { useViewport } from "@bookingcom/bui-react";
import { UIFlightData } from "@flights/types";
import { useCallback, useRef, useState } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "../../../store/searchCriteria/selectors";
import { getSearchDisplayOptions } from "../../../store/searchResults/selectors";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa";
import { useCardSegmentsCondensedController } from "./components/elements/CardSegmentCondensed/useCardSegmentsCondensed.controller";
import { trackWithDefaultStage } from "@flights/et-universal";
import useFetchBrandedFares from "../../../branded-fares/hooks/useFetchBrandedFares";
import useIsInViewport from "../../../hooks/useIsInViewport";
import { carrierUtils } from "@flights/web-api-utils-universal";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getCampaignBestOfferHighlightConfig } from "../../../utils/flightCampaignConfig";
import useLocaleContext from "../../../hooks/useLocaleContext";
import flights_web_flight_card_update_desktop from "../../../utils/experiments/funnel/flights_web_flight_card_update_desktop";
import { FlightCardHighlightStyle } from "./components/FlightCardHighlight";

const useFlightCard2Controller = (flight: UIFlightData) => {
  const i18n = useI18n();
  const { isInternal } = useGlobalContext();
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const flightCardBoundRef = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(flightCardBoundRef.current);
  const { isRTL } = useLocaleContext();

  const { adults, children } = useSelector(getSearchCriteria);
  const displayOptions = useSelector(getSearchDisplayOptions);

  const hasPrefetchedBrandedFareOffers = !!flight.brandedFareOffers && flight.brandedFareOffers?.length > 0;
  const canShowFareSelector = flight.requestableBrandedFares || hasPrefetchedBrandedFareOffers;

  const showFareSelectorDefault = canShowFareSelector && displayOptions?.brandedFaresShownByDefault;
  const [showFareSelector, setShowFareSelector] = useState(showFareSelectorDefault);
  const toggleShowFareSelector = useCallback(() => {
    if (showFareSelector) {
      flights_apex_web_ancillaries_micro_conversion_aa.goals.show_fare_options_sr();
      flights_web_flight_card_update_desktop.goals.open_bf_carousel();
    }

    setShowFareSelector(!showFareSelector);
  }, [showFareSelector]);

  const { isSmall } = useViewport();
  const isResponsiveFixesSm = isSmall && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);
  const { segmentsData, carrierText } = useCardSegmentsCondensedController(flight.segments);

  const travellersCount = adults + children.length;
  const carriers = carrierUtils.getMarketingCarriers(flight.segments);

  const highlightFireflySR =
    isInternal &&
    flight.token.startsWith("c8e49_") &&
    new URLSearchParams(window.location.search).get("highlight_firefly") === "1";

  const isCrossSellOffer = flight.highlightInfo?.highlight && flight.highlightInfo.reason === "CROSS_SELL_OFFER";
  const isGeniusHighlightedOffer =
    flight.highlightInfo?.highlight && flight.highlightInfo.reason === "GENIUS_AIRLINE_HIGHLIGHTED_OFFER";
  const isGeniusUprankedOffer =
    flight.highlightInfo?.highlight &&
    (flight.highlightInfo.reason === "GENIUS_AIRLINE_FARE_OFFER" ||
      flight.highlightInfo.reason === "GENIUS_AIRLINE_UPRANKED_OFFER");

  const shouldPinOfferOnSr = isCrossSellOffer || isGeniusHighlightedOffer || isGeniusUprankedOffer;

  const getPinnedHighlightText = () => {
    if (isGeniusHighlightedOffer || isGeniusUprankedOffer) return flight.highlightInfo?.message ?? "";
    return flight.highlightInfo?.message || i18n.trans(t("flights_meta_sr_pinned_flight_label"));
  };
  const pinnedHighlightText = getPinnedHighlightText();

  const pinnedInfoTooltip =
    isGeniusUprankedOffer && flight.highlightInfo?.infoMessage
      ? {
          content: flight.highlightInfo.infoMessage.subtitle,
          ariaLabel: i18n.trans(t("a11y_flights_sr_featured_flight_more_informatio_popup")),
          title: flight.highlightInfo.infoMessage.title
        }
      : undefined;

  const pinnedHighlightStyle = isGeniusHighlightedOffer
    ? FlightCardHighlightStyle.NEUTRAL
    : FlightCardHighlightStyle.PRIMARY;

  const { bestOfferHighlightStyle, bestOfferHighlightText } = getCampaignBestOfferHighlightConfig(
    flight.campaignDisplay?.bestOfferHighlight
  );

  useFetchBrandedFares(
    flight.token,
    isInViewport,
    !!flight.requestableBrandedFares,
    true,
    !!flight.brandedFareInfo?.fareName,
    carriers
  );

  return {
    breakdownOpen,
    setBreakdownOpen,
    travellersCount,
    canShowFareSelector,
    toggleShowFareSelector,
    showFareSelector,
    isResponsiveFixesSm,
    segmentsData,
    carrierText,
    highlightFireflySR,
    shouldPinOfferOnSr,
    bestOfferHighlightStyle,
    bestOfferHighlightText,
    isRTL,
    flightCardBoundRef,

    priceLabel: i18n.trans(travellersCount > 1 ? t("flights_sr_rt_price_per") : t("flights_sr_price_round_trip")),
    priceAriaLabel: i18n.trans(t("flights_a11y_price_breakdown_close_sr")),
    fareOptionsCta: i18n.trans(t("flights_sr_cta_fares")),
    viewDetailsCta: i18n.trans(t("flights_sr_cta_details")),
    flightPinnedLabel: pinnedHighlightText,
    isGeniusHighlightedOffer,
    isGeniusUprankedOffer,
    pinnedInfoTooltip,
    pinnedHighlightStyle
  };
};

export default useFlightCard2Controller;
